/**
 * Tier Discount utilities.
 *
 * Automatic discounts based on user loyalty tier (from userTiers feature).
 * Different from priceRules (time-based) and voucherCodes (code-based).
 *
 * Discount config from TIER_DISCOUNTS_JSON env var:
 * {
 *   "bronze": { "pct": 5 },
 *   "silver": { "pct": 10 },
 *   "gold":   { "pct": 15 },
 *   "platinum": { "pct": 20 }
 * }
 *
 * Discount applied automatically at checkout for logged-in users.
 */

export const DEFAULT_TIER_DISCOUNTS = {
  bronze: { pct: 5 },
  silver: { pct: 10 },
  gold: { pct: 15 },
  platinum: { pct: 20 },
};

/**
 * Get user's current tier from user object.
 * @param {Object|null} user
 * @returns {string|null}
 */
export const getUserTier = (user) =>
  user?.attributes?.profile?.publicData?.tier || null;

/**
 * Get discount percentage for a tier.
 * @param {string} tier
 * @param {Object} [tierDiscounts]
 * @returns {number} — 0 if no discount
 */
export const getTierDiscountPct = (tier, tierDiscounts = DEFAULT_TIER_DISCOUNTS) => {
  if (!tier || !tierDiscounts[tier]) return 0;
  return tierDiscounts[tier].pct || 0;
};

/**
 * Calculate discount amount.
 * @param {number} amountCents
 * @param {string} tier
 * @param {Object} [tierDiscounts]
 * @returns {{ discountCents: number, finalCents: number, pct: number }}
 */
export const calculateTierDiscount = (amountCents, tier, tierDiscounts = DEFAULT_TIER_DISCOUNTS) => {
  const pct = getTierDiscountPct(tier, tierDiscounts);
  const discountCents = Math.floor(amountCents * (pct / 100));
  return {
    discountCents,
    finalCents: amountCents - discountCents,
    pct,
  };
};

/**
 * Check if user qualifies for any discount.
 * @param {Object|null} user
 * @param {Object} [tierDiscounts]
 * @returns {boolean}
 */
export const hasDiscount = (user, tierDiscounts = DEFAULT_TIER_DISCOUNTS) =>
  getTierDiscountPct(getUserTier(user), tierDiscounts) > 0;
